import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, Zap, LogIn, Check, Loader2, Pencil, Clock, Gamepad2, User, Crown } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import LiveFeed from "../components/LiveFeed";
import LiveTournamentStrip from "../components/LiveTournamentStrip";
import DiscordLiveWidget from "../components/DiscordLiveWidget";
import PlayOfTheWeekShowcase from "../components/PlayOfTheWeekShowcase";
import DiscordCTA from "../components/DiscordCTA";
import Footer from "../components/Footer";
import AuthModal from "../components/AuthModal";
import JoinTournamentModal from "../components/JoinTournamentModal";
import { api, formatError, resolveAsset } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLiveWS } from "../hooks/useLiveWS";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

// Compact human date: "Apr 25 · 8:00 PM" — uses the viewer's local timezone.
function formatStartsAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch {
    return "";
  }
}

export const SpotlightCountdown = ({ iso, id }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = Math.floor((target - now) / 1000);
  let label;
  if (diff <= 0) label = "Starting soon";
  else {
    const d = Math.floor(diff / 86400), h = Math.floor((diff % 86400) / 3600);
    const m = Math.floor((diff % 3600) / 60), s = diff % 60;
    label = d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  }
  return (
    <span data-testid={`featured-countdown-${id}`}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-red-500/15 border border-red-500/30 text-red-200 tabular-nums">
      <Clock size={10} /> {diff > 0 ? `in ${label}` : label}
    </span>
  );
};

export const SpotlightCheckIn = ({ tournament, user }) => {
  const navigate = useNavigate();
  const [state, setState] = useState("idle");
  if (tournament.checkin_status !== "open" || ["live", "finished"].includes(tournament.status)) return null;
  const go = async () => {
    if (!user) return navigate(`/tournaments/${tournament.id}`);
    setState("busy");
    try {
      await api.post(`/tournaments/${tournament.id}/checkin`);
      toast.success("You're checked in!");
      setState("done");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
      setState("idle");
    }
  };
  return (
    <button onClick={go} disabled={state !== "idle"} data-testid={`spotlight-checkin-${tournament.id}`}
      className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono rounded-full px-3 py-1 border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 transition-colors disabled:opacity-80">
      {state === "done" ? <Check size={11} /> : state === "busy" ? <Loader2 size={11} className="animate-spin" /> : <span className="live-dot !bg-emerald-400" />}
      {state === "done" ? "Checked in" : "Check in"}
    </button>
  );
};

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState({ prize: 0, teams: 0, matches: 0, live: false });
  const [authOpen, setAuthOpen] = useState(false);
  const [joinModalTournament, setJoinModalTournament] = useState(null);
  const [pendingJoin, setPendingJoin] = useState(null); // tournament id to auto-join after signin
  const [joining, setJoining] = useState(null);
  const [myTeams, setMyTeams] = useState({}); // { tournamentId: team }
  const [myRequests, setMyRequests] = useState({}); // { tournamentId: "pending" | "rejected" | ... }
  const [champions, setChampions] = useState([]); // recent finished tournaments with winner team

  const load = async () => {
    const [{ data: ts }, { data: f }, { data: champs }] = await Promise.all([
      api.get("/tournaments"),
      api.get("/feed", { params: { limit: 12 } }),
      api.get("/champions/recent", { params: { limit: 4 } }).catch(() => ({ data: [] })),
    ]);
    setTournaments(ts);
    setFeed(f);
    setChampions(champs || []);
    // Sum totals across ALL tournaments so the hero stat matches any single
    // admin panel number (not only the featured 3).
    const totalsByTid = await Promise.all(
      ts.map((t) => api.get(`/tournaments/${t.id}`).then((r) => r.data)),
    );
    const teamCount = totalsByTid.reduce((a, d) => a + (d.teams?.length || 0), 0);
    const matchCount = totalsByTid.reduce((a, d) => a + (d.matches?.length || 0), 0);
    // Build map of my team per tournament (only needed for featured strip)
    const mine = {};
    totalsByTid.forEach((d) => {
      if (user) {
        const mt = d.teams.find((tm) => tm.owner_id === user.id);
        if (mt) mine[d.tournament.id] = mt;
      }
    });
    setMyTeams(mine);
    // Fetch this user's join-request status for every tournament (only the
    // featured ones visible on the homepage need it, but the list is small).
    if (user) {
      const pairs = await Promise.all(
        ts.map((t) =>
          api
            .get(`/tournaments/${t.id}/my-request`)
            .then((r) => [t.id, r.data?.status || null])
            .catch(() => [t.id, null]),
        ),
      );
      const reqMap = {};
      pairs.forEach(([tid, status]) => {
        if (status) reqMap[tid] = status;
      });
      setMyRequests(reqMap);
    } else {
      setMyRequests({});
    }
    setStats({
      prize: ts.reduce((a, t) => a + (t.prize_pool || 0), 0),
      teams: teamCount,
      matches: matchCount,
      live: ts.some((t) => t.status === "live"),
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);

  // If user just signed in and had a pending join, auto-execute it
  useEffect(() => {
    if (user && pendingJoin) {
      const tid = pendingJoin;
      setPendingJoin(null);
      doJoin(tid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pendingJoin]);

  const doJoin = async (tid) => {
    setJoining(tid);
    try {
      const { data } = await api.post(`/tournaments/${tid}/join`);
      if (data.already_joined) {
        toast.success("You're already in the roster");
      } else if (data.pending) {
        toast.success("Request sent — an admin will approve you shortly");
      }
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setJoining(null);
    }
  };

  const handleJoinClick = (t) => {
    if (!user) {
      setPendingJoin(t.id);
      setAuthOpen(true);
      return;
    }
    // Open the small form modal that collects in-game username + player ID
    setJoinModalTournament(t);
  };

  const renameMyTeam = async (tid) => {
    const my = myTeams[tid];
    if (!my) return;
    const next = window.prompt("New team name (3–40 characters)", my.name);
    if (!next) return;
    const trimmed = next.trim();
    if (trimmed.length < 3 || trimmed.length > 40) {
      toast.error("Team name must be 3–40 characters");
      return;
    }
    try {
      const r = await api.put(`/teams/${my.id}`, { name: trimmed });
      setMyTeams((prev) => ({ ...prev, [tid]: r.data }));
      toast.success(`Team renamed to "${trimmed}"`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
  };

  // derive: does the current user already have a team in this tournament?
  const isJoined = (t) =>
    Boolean(user && (t.participant_ids || []).includes(user.id));

  useLiveWS((msg) => {
    if (msg.event === "feed") {
      setFeed((prev) => [msg.data, ...prev].slice(0, 15));
    } else if (
      msg.event === "tournament_updated" ||
      msg.event === "tournament_created" ||
      msg.event === "bracket_generated"
    ) {
      load();
    }
  });

  const featured = tournaments.filter((t) => t.status === "live" || t.status === "scheduled").slice(0, 3);
  const liveTournament = tournaments.find((t) => t.status === "live");
  const liveTournaments = tournaments.filter((t) => t.status === "live");
  const fallbackTournament = liveTournament || tournaments[0];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      {/* Live tournament strip — always on top, only when a tournament is live */}
      {liveTournament && (
        <section className="max-w-7xl mx-auto px-6 pt-6 relative z-20">
          <LiveTournamentStrip
            tournament={liveTournament}
            extraLiveCount={Math.max(0, liveTournaments.length - 1)}
          />
        </section>
      )}

      {/* Featured Play of the Week clip — self-hides if none featured */}
      {/* Removed thin strip at top; showcase lives in place of the old stats block */}

      <Hero
        stats={stats}
        discordUrl={DISCORD_URL}
        liveTournamentId={fallbackTournament?.id}
        hasLive={Boolean(liveTournament)}
        showStats={false}
      />

      {/* Featured tournaments */}
      <section className="max-w-7xl mx-auto px-6 -mt-14 relative z-10">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-red-400 font-mono">
              Featured
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">
              Tournaments in spotlight
            </h2>
          </div>
          <Link to="/tournaments" data-testid="home-view-all-tournaments" className="btn-ghost-glass !py-2 !px-4 !text-xs">
            View all <ArrowRight size={12} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`featured-${t.id}`}
                  className="glass-red rounded-2xl p-5 hover:shadow-[0_0_28px_rgba(230,0,0,0.25)] transition-all relative overflow-hidden"
                >
                  {t.banner_url && (
                    <>
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          backgroundImage: `url(${resolveAsset(t.banner_url)})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          opacity: 0.7,
                        }}
                      />
                      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-black/55 via-black/30 to-black/15" />
                    </>
                  )}
                  <div className="relative">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {t.status === "live" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-red-300">
                        <span className="live-dot" /> Live now
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-400">Upcoming</span>
                    )}
                    {t.game && (
                      <span
                        data-testid={`featured-game-${t.id}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-zinc-300 bg-white/5 border border-white/10 rounded-full px-2 py-0.5"
                      >
                        <Gamepad2 size={10} className="text-red-400/80" />
                        {t.game}
                      </span>
                    )}
                    <span
                      data-testid={`featured-mode-${t.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-red-200 bg-red-500/15 border border-red-500/30 rounded-full px-2 py-0.5"
                    >
                      {(() => {
                        const ts = Number(t.team_size || 1);
                        if (ts <= 1) return "Solo";
                        if (ts === 2) return "2v2";
                        if (ts === 3) return "3v3";
                        if (ts === 4) return "4v4";
                        return "5v5";
                      })()}
                    </span>
                    <SpotlightCheckIn tournament={t} user={user} />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2 leading-snug">{t.name}</h3>
                  <p className="text-sm text-zinc-300 mb-3 line-clamp-2">{t.description || "Competitive tournament"}</p>
                  {t.starts_at && (
                    <div
                      data-testid={`featured-starts-${t.id}`}
                      className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-zinc-400 mb-1.5"
                    >
                      <Calendar size={11} className="text-red-400/80" />
                      <span>Starts {formatStartsAt(t.starts_at)}</span>
                      {t.status !== "live" && t.status !== "finished" && <SpotlightCountdown iso={t.starts_at} id={t.id} />}
                    </div>
                  )}
                  {t.host && (
                    <div
                      data-testid={`featured-host-${t.id}`}
                      className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-zinc-400 mb-3"
                    >
                      <User size={11} className="text-red-400/80" />
                      <span>Hosted by <span className="text-zinc-200 font-semibold normal-case tracking-normal">{t.host}</span></span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-red-500/15 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Prize</div>
                      <div className="font-display font-bold text-lg text-red-300">
                        ${(t.prize_pool || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {t.status !== "live" && t.status !== "finished" && (
                        isJoined(t) ? (
                          <div className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 pl-3 pr-1 py-1">
                            <Check size={12} />
                            <span
                              data-testid={`featured-joined-${t.id}`}
                              className="text-[11px] font-mono uppercase tracking-widest"
                            >
                              Joined{myTeams[t.id]?.name ? ` · ${myTeams[t.id].name.slice(0, 14)}` : ""}
                            </span>
                            {myTeams[t.id] && (
                              <button
                                onClick={() => renameMyTeam(t.id)}
                                data-testid={`featured-rename-team-${t.id}`}
                                className="h-5 w-5 rounded-full flex items-center justify-center hover:bg-white/10 text-emerald-200"
                                title="Rename team"
                              >
                                <Pencil size={10} />
                              </button>
                            )}
                          </div>
                        ) : myRequests[t.id] === "pending" ? (
                          <div
                            data-testid={`featured-pending-${t.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 px-3 py-1 text-[11px] font-mono uppercase tracking-widest"
                            title="Waiting for an admin to approve your request"
                          >
                            <Clock size={11} /> Pending
                          </div>
                        ) : myRequests[t.id] === "rejected" ? (
                          <div
                            data-testid={`featured-rejected-${t.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 px-3 py-1 text-[11px] font-mono uppercase tracking-widest"
                          >
                            Rejected
                          </div>
                        ) : (
                          <button
                            onClick={() => handleJoinClick(t)}
                            disabled={joining === t.id}
                            data-testid={`featured-join-${t.id}`}
                            className="btn-liquid !py-2 !px-3 !text-xs disabled:opacity-60"
                          >
                            {joining === t.id ? <Loader2 size={12} className="animate-spin" /> : (user ? <Zap size={12} /> : <LogIn size={12} />)}
                            {user ? "Request to join" : "Sign in to join"}
                          </button>
                        )
                      )}
                      <Link to={`/tournament/${t.id}/bracket`} className="btn-ghost-glass !py-2 !px-3 !text-xs">
                        <Zap size={12} /> Bracket
                      </Link>
                    </div>
                  </div>
                  </div>
                </motion.div>
              ))}
              {featured.length === 0 && (
                <div className="sm:col-span-2 lg:col-span-3 glass-dark rounded-2xl p-8 text-center text-zinc-500">
                  No featured tournaments yet.
                </div>
              )}
        </div>
      </section>

      {/* Live feed — full width under featured */}
      <section className="max-w-7xl mx-auto px-6 mt-10">
        <LiveFeed
          items={feed}
          onItemClick={(item) => {
            if (item.type === "clip") {
              navigate("/play-of-the-week");
            } else if (item.tournament_id) {
              navigate(`/tournaments/${item.tournament_id}`);
            }
          }}
        />
      </section>

      {/* Live Discord server widget — hides itself when server widget disabled. */}
      <DiscordLiveWidget fallbackInvite={DISCORD_URL} />

      {/* Recent Champions — only shown when there are finished tournaments. */}
      {champions.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 mt-10" data-testid="home-recent-champions">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-2 flex items-center gap-2">
                <Crown size={12} className="text-yellow-400" />
                Recent champions
              </div>
              <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight">
                Wall of glory
              </h2>
            </div>
            <Link
              to="/hall-of-fame"
              data-testid="home-champions-see-all"
              className="text-xs font-mono uppercase tracking-widest text-red-300 hover:text-red-200 flex items-center gap-1"
            >
              Hall of Fame <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {champions.map((c) => (
              <Link
                key={c.tournament_id}
                to="/hall-of-fame"
                data-testid={`champion-card-${c.tournament_id}`}
                className="group relative overflow-hidden glass-dark rounded-xl p-3 hover:border-red-500/40 hover:shadow-[0_0_20px_rgba(230,0,0,0.2)] transition-all"
              >
                <div className="absolute -top-10 -right-10 h-24 w-24 rounded-full bg-red-500/10 blur-2xl pointer-events-none group-hover:bg-red-500/15" />
                <div className="relative flex items-center gap-3">
                  <div className="relative shrink-0">
                    <Crown
                      size={12}
                      className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-yellow-400 drop-shadow-[0_0_4px_rgba(255,205,0,0.9)]"
                    />
                    {c.team.logo_url ? (
                      <img
                        src={resolveAsset(c.team.logo_url)}
                        alt=""
                        className="h-11 w-11 rounded-full object-cover border border-red-500/50 shadow-[0_0_12px_rgba(230,0,0,0.3)]"
                      />
                    ) : (
                      <div
                        className="h-11 w-11 rounded-full flex items-center justify-center border border-red-500/50 shadow-[0_0_12px_rgba(230,0,0,0.3)] font-display font-black text-xs text-white"
                        style={{ background: c.team.color || "linear-gradient(180deg, #b91c1c, #450a0a)" }}
                      >
                        {(c.team.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] uppercase tracking-widest font-mono text-yellow-400 leading-none mb-0.5">
                      Champion
                    </div>
                    <div className="font-display font-bold text-sm text-white truncate">
                      {c.team.name || "Unknown team"}
                    </div>
                    <div className="text-[11px] text-zinc-400 truncate" title={c.tournament_name}>
                      {c.tournament_name}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {c.game && (
                        <span className="text-[8px] uppercase tracking-widest font-mono text-zinc-400 bg-white/5 border border-white/10 rounded-full px-1.5 py-0.5">
                          {c.game}
                        </span>
                      )}
                      {c.prize_pool > 0 && (
                        <span className="text-[8px] uppercase tracking-widest font-mono text-emerald-300 bg-emerald-950/40 border border-emerald-500/30 rounded-full px-1.5 py-0.5">
                          ${c.prize_pool.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  {(c.ended_at || c.runner_up) && (
                    <div className="flex flex-col items-end gap-1 pl-2 border-l border-white/5 shrink-0 min-w-0">
                      {c.ended_at && (
                        <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500 leading-none">
                          {(() => {
                            try {
                              return new Date(c.ended_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
                            } catch { return ""; }
                          })()}
                        </div>
                      )}
                      {c.runner_up && (
                        <div className="flex items-center gap-1.5 max-w-[140px]">
                          <span className="text-[9px] uppercase tracking-widest font-mono text-zinc-500 leading-none">Def.</span>
                          {c.runner_up.logo_url ? (
                            <img
                              src={resolveAsset(c.runner_up.logo_url)}
                              alt=""
                              className="h-5 w-5 rounded-full object-cover border border-white/10"
                            />
                          ) : (
                            <div
                              className="h-5 w-5 rounded-full flex items-center justify-center border border-white/10 font-display font-black text-[8px] text-white"
                              style={{ background: c.runner_up.color || "linear-gradient(180deg, #52525b, #18181b)" }}
                            >
                              {(c.runner_up.name || "?").slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <span className="text-[11px] text-zinc-300 truncate">{c.runner_up.name}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Bracket generator teaser — clickable, requires auth. */}
      <section className="max-w-7xl mx-auto px-6 mt-10" data-testid="home-bracket-generator-teaser">
        <div className="relative overflow-hidden rounded-3xl border border-red-500/20 bg-gradient-to-br from-[#120303] via-black to-[#0a0303] p-6 md:p-10">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-red-600/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-[1fr_1.4fr] gap-6 md:gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-3 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                Free tool · live now
              </div>
              <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">
                Try our bracket generator
              </h2>
              <p className="text-zinc-400 mt-4 text-sm md:text-base leading-relaxed max-w-lg">
                Build your own bracket in seconds. Pick a format, paste team names,
                share the result as a PNG. Saved to your account.
              </p>
              {user ? (
                <button
                  onClick={() => navigate("/bracket-generator")}
                  data-testid="home-bracket-generator-cta"
                  className="btn-liquid mt-6 !py-2.5 !px-5"
                >
                  <Zap size={14} /> Open bracket generator
                </button>
              ) : (
                <button
                  onClick={() => setAuthOpen(true)}
                  data-testid="home-bracket-generator-signin"
                  className="btn-liquid mt-6 !py-2.5 !px-5"
                >
                  <LogIn size={14} /> Sign in to use it
                </button>
              )}
            </div>
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-red-500/5 blur-2xl" />
              <img
                src="/assets/bracket-preview.png"
                alt="Bracket generator preview"
                className="relative w-full rounded-2xl border border-white/10 shadow-[0_0_60px_rgba(230,0,0,0.25)]"
                loading="lazy"
                data-testid="home-bracket-generator-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Play of the Week showcase / status bar — moved below the Bracket Generator
           per user request. Falls back to a compact stats row when no clip is featured. */}
      <PlayOfTheWeekShowcase stats={stats} />

      <section className="max-w-7xl mx-auto px-6 py-16">
        <DiscordCTA discordUrl={DISCORD_URL} />
      </section>

      <Footer discordUrl={DISCORD_URL} />
      {authOpen && <AuthModal open={true} onClose={() => setAuthOpen(false)} />}
      <JoinTournamentModal
        tournament={joinModalTournament}
        open={!!joinModalTournament}
        onClose={() => setJoinModalTournament(null)}
        onSubmitted={load}
      />
    </div>
  );
}
